/**
 * Construye la lista de "Información clínica relevante" para el sidebar del
 * informe profesional. NO decide nada — solo expone datos contextuales para
 * la consideración del profesional.
 *
 * Devuelve una lista de items: { label: string, value?: string, kind?: string }
 * - label: etiqueta visible.
 * - value: dato (opcional, ej. "158 mg/dL").
 * - kind: 'demographic' | 'lifestyle' | 'lab' | 'ergometry' | 'limitations' | 'general'
 *         (sólo informativo, no afecta render).
 */

import { getActivityLabel } from '@/utils/labels';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export function buildClinicalInfo(ev) {
  if (!ev) return [];
  const items = [];

  // Edad, sexo, IMC y presión NO se repiten acá: ya figuran en el encabezado
  // de datos de la persona. Esta lista es para lo que NO está en otro lado.
  if (ev.activity_level) {
    items.push({
      label: 'Nivel de actividad',
      value: getActivityLabel(ev.activity_level),
      kind: 'lifestyle',
    });
  }

  // --- Estilo de vida / antecedentes ---
  if (ev.smoking) items.push({ label: 'Tabaquismo activo', kind: 'lifestyle' });

  if (ev.hypertension) {
    const treated = ev.hypertension_treatment === true ? ' (en tratamiento)' : ' (sin tratamiento informado)';
    items.push({
      label: `HTA autorreportada${treated}`,
      kind: 'lifestyle',
    });
  }

  if (ev.dyslipidemia) {
    const treatedDys = ev.dyslipidemia_treatment === true ? ' (en tratamiento)' : '';
    items.push({
      label: `Dislipemia autorreportada${treatedDys}`,
      kind: 'lifestyle',
    });
  }

  // Con diabetes declarada la prediabetes ya no aplica.
  if (ev.prediabetes && !ev.diabetes) {
    items.push({ label: 'Prediabetes o glucemia alterada autorreportada', kind: 'lifestyle' });
  }

  if (ev.family_history) {
    items.push({
      label: 'Antecedente familiar de IAM / muerte súbita precoz',
      kind: 'lifestyle',
    });
  }

  // --- Antropometría ---
  const imc = num(ev.imc);
  if (imc !== null) {
    let cat = 'normopeso';
    if (imc >= 40) cat = 'obesidad III';
    else if (imc >= 35) cat = 'obesidad II';
    else if (imc >= 30) cat = 'obesidad I';
    else if (imc >= 25) cat = 'sobrepeso';
    else if (imc < 18.5) cat = 'bajo peso';
    items.push({ label: `IMC ${imc.toFixed(1)} (${cat})`, kind: 'demographic' });
  }

  const waist = num(ev.waist_circumference);
  const whtr = num(ev.waist_height_ratio);
  if (waist && (ev.adiposity_status === 'elevated_central' || (whtr !== null && whtr >= 0.5))) {
    const whtrTxt = whtr !== null ? `, índice cintura/altura ${whtr.toFixed(2)}` : '';
    items.push({
      label: `Adiposidad central (cintura ${waist} cm${whtrTxt})`,
      kind: 'demographic',
    });
  }

  // --- Sedentarismo prolongado ---
  const sedHours = num(ev.sedentary_hours);
  if (sedHours !== null && sedHours >= 8) {
    items.push({ label: `Sedentarismo prolongado: ${sedHours} h/día sentado`, kind: 'lifestyle' });
  }

  // --- Laboratorio (solo valores fuera de rango) ---
  if (ev.has_lab_recent) {
    const ldl = num(ev.ldl);
    if (ldl !== null && ldl >= 130) {
      items.push({
        label: `LDL elevado: ${ldl} mg/dL`,
        kind: 'lab',
      });
    }
    const hdl = num(ev.hdl);
    if (hdl !== null && hdl < 40) {
      items.push({ label: `HDL bajo: ${hdl} mg/dL`, kind: 'lab' });
    }
    const trig = num(ev.triglycerides);
    if (trig !== null && trig >= 150) {
      items.push({
        label: trig >= 200 ? `Triglicéridos elevados: ${trig} mg/dL` : `Triglicéridos limítrofes: ${trig} mg/dL`,
        kind: 'lab',
      });
    }
    const chol = num(ev.cholesterol_total);
    if (chol !== null && chol >= 200) {
      items.push({
        label: chol >= 240 ? `Colesterol total elevado: ${chol} mg/dL` : `Colesterol total limítrofe: ${chol} mg/dL`,
        kind: 'lab',
      });
    }
    const glu = num(ev.glucose);
    if (glu !== null && glu >= 100) {
      items.push({
        label: glu >= 126 ? `Glucemia elevada: ${glu} mg/dL` : `Glucemia en rango de prediabetes: ${glu} mg/dL`,
        kind: 'lab',
      });
    }
  }

  // --- Ergometría ---
  if (ev.has_stress_test) {
    const mets = num(ev.stress_test_mets);
    if (mets !== null) items.push({ label: `Ergometría: ${mets} METs`, kind: 'ergometry' });
    const fcPico = num(ev.stress_test_fc_pico);
    if (fcPico !== null) {
      items.push({ label: `FC pico (PEG): ${fcPico} lpm — usada para Karvonen`, kind: 'ergometry' });
    }
    const dp = num(ev.stress_test_doble_producto);
    if (dp !== null) items.push({ label: `Doble producto pico: ${dp}`, kind: 'ergometry' });
    // Nota: "Quiebre del doble producto" se sigue guardando en BD
    // (stress_test_quiebre_dp) pero no se muestra en el Informe Final ni PDFs.
  }

  // --- Limitaciones osteoarticulares ---
  if (ev.has_limitations && ev.affected_joint) {
    items.push({ label: `Limitaciones osteoarticulares: ${ev.affected_joint}`, kind: 'limitations' });
  }

  return items;
}
